'use client';

/**
 * AdaptiveSpotlight — one quiet machine-voice line that surfaces the two or
 * three destinations matching the visitor's inferred intent. Purely additive:
 * it never reorders or hides anything else, renders nothing for unknown or
 * explorer intent, and can be dismissed for the session.
 *
 * Mount it on the home page, above the zones grid.
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useWorld } from '../../lab/store';
import { site } from '../../content/site';
import type { IntentId } from '../../lab/types';
import { useLang } from '../../i18n/client';
import { getDict } from '../../i18n/dictionary';
import { localePath, type Lang } from '../../i18n/config';
import styles from './AdaptiveSpotlight.module.css';

/** sessionStorage flag: the visitor dismissed the spotlight this session. */
const DISMISS_KEY = 'slp-spotlight-dismissed';

/** Keys into the nav chrome dictionary, for internal link labels. */
type NavKey = 'research' | 'publications' | 'agents' | 'knowledge' | 'projects' | 'contact';

interface SpotLink {
  /** Internal link: label resolved from dict.nav, href prefixed with locale. */
  navKey?: NavKey;
  /** External link: literal label (kept in Latin, e.g. GitHub). */
  label?: string;
  /** Logical path (internal) or absolute URL (external). */
  href: string;
  external?: boolean;
}

interface SpotlightSpec {
  links: SpotLink[];
}

const SPECS: Partial<Record<IntentId, SpotlightSpec>> = {
  researcher: {
    links: [
      { navKey: 'research', href: '/research' },
      { navKey: 'publications', href: '/publications' },
    ],
  },
  developer: {
    links: [
      { label: 'GitHub', href: site.github, external: true },
      { navKey: 'agents', href: '/agents' },
      { navKey: 'knowledge', href: '/knowledge' },
    ],
  },
  partner: {
    links: [
      { navKey: 'projects', href: '/projects' },
      { navKey: 'contact', href: '/contact' },
    ],
  },
};

/**
 * The sentence frame. Full spotlight lines also live in the shared dictionary
 * (dict.spotlight.*), but they fold the destination names into one static
 * sentence; this component instead composes prefix + noun + a live confidence
 * value + real, clickable links, so the frame is kept here in pieces and the
 * link labels come from dict.nav. The English output is unchanged.
 */
const copy: Record<Lang, {
  aria: string;
  dismissAria: string;
  prefix: string;
  prioritized: string;
  noun: Partial<Record<IntentId, string>>;
}> = {
  en: {
    aria: 'Adaptive interface signal',
    dismissAria: 'Dismiss adaptive signal',
    prefix: 'Signal detected: ',
    prioritized: '· prioritized:',
    noun: {
      researcher: 'research interest',
      developer: 'engineering interest',
      partner: 'partnership interest',
    },
  },
  ru: {
    aria: 'Сигнал адаптивного интерфейса',
    dismissAria: 'Скрыть адаптивный сигнал',
    prefix: 'Сигнал: ',
    prioritized: '· в приоритете:',
    noun: {
      researcher: 'исследовательский интерес',
      developer: 'инженерный интерес',
      partner: 'интерес к сотрудничеству',
    },
  },
};

export default function AdaptiveSpotlight() {
  const lang = useLang();
  const dict = getDict(lang);
  const c = copy[lang];
  const intent = useWorld((s) => s.visitor.intent);
  const confidence = useWorld((s) => s.visitor.intentConfidence);
  const [dismissed, setDismissed] = useState(false);

  // Safe from hydration mismatch: intent is 'unknown' on both server and
  // first client render, so nothing shows until inference runs client-side.
  useEffect(() => {
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true);
    } catch {
      // storage unavailable — dismissal just won't persist
    }
  }, []);

  const spec = SPECS[intent];
  if (!spec || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // ignore
    }
  };

  const conf = confidence[intent];
  const noun = c.noun[intent] ?? '';

  return (
    <div className={styles.spotlight} role="note" aria-label={c.aria}>
      <span className={styles.text}>
        {c.prefix}{noun}
        {conf !== undefined && (
          <span className={styles.conf}> · conf {conf.toFixed(2)}</span>
        )}{' '}
        {c.prioritized}
      </span>
      {spec.links.map((l) => {
        const label = l.navKey ? dict.nav[l.navKey] : l.label ?? '';
        return l.external ? (
          <a
            key={l.href}
            href={l.href}
            className={styles.link}
            target="_blank"
            rel="noopener noreferrer"
          >
            {label} ↗
          </a>
        ) : (
          <Link key={l.href} href={localePath(lang, l.href)} className={styles.link}>
            {label} →
          </Link>
        );
      })}
      <button
        type="button"
        className={styles.close}
        onClick={dismiss}
        aria-label={c.dismissAria}
      >
        ✕
      </button>
    </div>
  );
}
